import { useCallback, useEffect, useRef, useState } from 'react';
import Player from '@vimeo/player';
import { parseVimeoEmbedRef, vimeoPlayerUrl } from '../../utils/vimeo-embed-ref';

export type PeersonaIntroVideoPhase = 'poster' | 'loading' | 'playing' | 'paused';

export interface VideoDimensions {
  width: number;
  height: number;
}

export interface UsePeersonaIntroVideoResult {
  containerRef: React.RefObject<HTMLDivElement>;
  phase: PeersonaIntroVideoPhase;
  togglePlayPause: () => void;
  /** True source dimensions from the player once ready. */
  videoDimensions: VideoDimensions | null;
}

/** Lazy Vimeo embed — player only mounts once `activated` flips true. */
export function usePeersonaIntroVideo(
  welcomeVideoVimeoId: string | null | undefined,
  activated: boolean,
): UsePeersonaIntroVideoResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [phase, setPhase] = useState<PeersonaIntroVideoPhase>('poster');
  const [videoDimensions, setVideoDimensions] = useState<VideoDimensions | null>(null);

  const destroyPlayer = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    try {
      player.destroy();
    } catch {
      // already destroyed
    }
    playerRef.current = null;
  }, []);

  useEffect(() => {
    if (activated) return;
    destroyPlayer();
    setPhase('poster');
    setVideoDimensions(null);
  }, [activated, welcomeVideoVimeoId, destroyPlayer]);

  useEffect(() => {
    if (!activated) return;
    const parsed = parseVimeoEmbedRef(welcomeVideoVimeoId);
    if (!parsed || !containerRef.current) return;

    setPhase('loading');
    setVideoDimensions(null);
    destroyPlayer();

    const options: Record<string, unknown> = {
      controls: false,
      title: false,
      byline: false,
      portrait: false,
      autoplay: true,
      responsive: true,
      dnt: true,
    };
    if (parsed.privacyHash) {
      options.url = vimeoPlayerUrl(parsed);
    } else {
      options.id = parseInt(parsed.videoId, 10);
    }

    let mounted = true;
    let player: Player;
    try {
      player = new Player(containerRef.current, options);
    } catch (error) {
      console.error('Peersona intro Vimeo player constructor error:', error);
      setPhase('poster');
      return;
    }
    playerRef.current = player;

    const onPlaying = () => mounted && setPhase('playing');
    const onPause = () => mounted && setPhase('paused');
    const onEnded = () => mounted && setPhase('paused');

    player.on('playing', onPlaying);
    player.on('pause', onPause);
    player.on('ended', onEnded);

    player
      .ready()
      .then(async () => {
        if (!mounted) return;
        const [width, height] = await Promise.all([player.getVideoWidth(), player.getVideoHeight()]);
        if (width > 0 && height > 0) {
          setVideoDimensions({ width, height });
        }
      })
      .catch((error) => {
        console.error('Peersona intro Vimeo player failed to initialize:', error);
        if (mounted) setPhase('poster');
      });

    return () => {
      mounted = false;
      player.off('playing', onPlaying);
      player.off('pause', onPause);
      player.off('ended', onEnded);
      destroyPlayer();
    };
  }, [activated, welcomeVideoVimeoId, destroyPlayer]);

  const togglePlayPause = useCallback(async () => {
    const player = playerRef.current;
    if (!player) return;
    try {
      const paused = await player.getPaused();
      if (paused) {
        setPhase('loading');
        await player.play();
      } else {
        await player.pause();
      }
    } catch (error) {
      console.error('Peersona intro Vimeo toggle failed:', error);
    }
  }, []);

  return { containerRef, phase, togglePlayPause, videoDimensions };
}
